/**
 * A tiny TTL map, the browser-side twin of `TtlCache` in backend/app/api/satellite.py.
 *
 * Both layers cache because both layers cost: the server's entry saves an Earth Engine
 * round trip, this one saves even the request. The TTLs are deliberately the same numbers
 * (6 h for dates, 2 h for tile templates) — two caches with different lifetimes would let
 * the interface show a list the server has already replaced.
 *
 * Eviction is oldest-inserted-first, not least-recently-used. The entries are a handful of
 * date strings and one URL each; a smarter policy would be more code than the thing it
 * manages.
 */

interface Entry<T> {
  at: number;
  value: T;
}

export class TtlCache<T> {
  private readonly entries = new Map<string, Entry<T>>();

  constructor(
    private readonly ttlMs: number,
    private readonly maxEntries = 50,
    private readonly now: () => number = () => Date.now(),
  ) {}

  get(key: string): T | null {
    const entry = this.entries.get(key);
    if (!entry) return null;
    if (this.now() - entry.at > this.ttlMs) {
      this.entries.delete(key);
      return null;
    }
    return entry.value;
  }

  put(key: string, value: T): void {
    this.entries.set(key, { at: this.now(), value });
    if (this.entries.size <= this.maxEntries) return;
    const oldest = this.entries.keys().next();
    if (!oldest.done) this.entries.delete(oldest.value);
  }

  clear(): void {
    this.entries.clear();
  }

  get size(): number {
    return this.entries.size;
  }
}

/** Same numbers as backend/app/api/satellite.py DATES_TTL_SECONDS / TILES_TTL_SECONDS. */
export const DATES_TTL_MS = 6 * 60 * 60 * 1000;
export const TILES_TTL_MS = 2 * 60 * 60 * 1000;
